import * as React from 'react';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {Routing} from 'nutshell-core/routing';

import {Button} from 'shells/button/button';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
import {LightbulbRegularIcon} from 'shells/icon/svg/lightbulb-regular-icon';
import {PenRulerRegularIcon} from 'shells/icon/svg/pen-ruler-regular-icon';

import {PageControlBar} from '../../../client_modules/shells/react/components/control-bar/page-control-bar';
import {useShouldShowWizard} from '../../../util/hooks/use-should-show-wizard';

import {PageTitleBar} from '../../layout';
import {PageContainer} from '../../layout/page-container';
import {useGetLimitsByType} from '../../limits';
import {TitleBarLimits} from '../../limits/title-bar-limits';
import {OpenableModals, useOpenableModal} from '../../openable-modals';

import {useGetStagesetsForSalesAutomation} from '../graphql/hooks/use-get-stagesets-for-sales-automation';

import {CreatePipelineModal} from './create-pipeline-modal';
import {SalesAutomationPipelineList} from './sales-automation-pipeline-list';

type Props = ContextRouter;

export function SalesAutomationSetupHomeComponent(props: Props) {
    const {history, location} = props;
    const [isCreatingPipeline, setIsCreatingPipeline] = React.useState<boolean>(false);
    const [activeTab, setActiveTab] = React.useState<string>('ACTIVE');

    const {stagesets, isLoading, errorMessage} = useGetStagesetsForSalesAutomation();
    const {limits} = useGetLimitsByType('PIPELINES');
    const {shouldShow: canShowPipelineWizard} = useShouldShowWizard(
        'defaultStagesetOnboardingWizard'
    );
    const {onOpen: onOpenPipelineWizard} = useOpenableModal(OpenableModals.DESIGN_PIPELINE_WIZARD);
    const {onOpen: onOpenPipelinesFeatureGateModal} = useOpenableModal(
        OpenableModals.PIPELINES_FEATURE_GATE_MODAL
    );

    React.useEffect(() => {
        const params = Routing.deparam(location.search.replace(/^\?/, ''));

        if (params.wizard && canShowPipelineWizard) {
            setTimeout(() => {
                onOpenPipelineWizard();
            }, 1000);
        }
        if (params.tab && NutClientConfig.hasDraftPipelinesFF) {
            setActiveTab(params.tab.toUpperCase());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history, location, canShowPipelineWizard]);

    const handleClickCreateNewPipeline = React.useCallback(() => {
        if ((limits && limits.canAdd) || NutClientConfig.hasDraftPipelinesFF) {
            setIsCreatingPipeline(true);
        } else {
            onOpenPipelinesFeatureGateModal();
        }
    }, [limits, setIsCreatingPipeline, onOpenPipelinesFeatureGateModal]);

    const activeCount = React.useMemo(() => {
        return stagesets.filter((stageset) => stageset.status === 'ACTIVE' && !stageset.isArchived)
            .length;
    }, [stagesets]);

    const draftCount = React.useMemo(() => {
        return stagesets.filter(
            (stageset) =>
                (stageset.status === 'DRAFT' || stageset.status === 'RETIRED') &&
                !stageset.isArchived
        ).length;
    }, [stagesets]);

    return (
        <PageContainer
            title='Pipelines'
            noPadding={true}
            pageTitleBar={
                <PageTitleBar
                    title='Pipelines'
                    subheaderPrimaryText='Manage your team’s sales process and pipelines.'
                    titleBadge={
                        <TitleBarLimits
                            type='PIPELINES'
                            onClick={onOpenPipelinesFeatureGateModal}
                            showTrialUpgradeArrow={true}
                        />
                    }
                    buttons={
                        <Button onClick={handleClickCreateNewPipeline} variant='primary' size='big'>
                            + Add new pipeline
                        </Button>
                    }
                />
            }
            controlBar={
                NutClientConfig.hasDraftPipelinesFF && (
                    <ListPageControlBar
                        noVerticalPadding={true}
                        archiveTab={{
                            isActive: activeTab === 'ARCHIVED',
                            onChange: () => {
                                setActiveTab('ARCHIVED');
                                history.replace({
                                    pathname: location.pathname,
                                    search: `?tab=archived`,
                                });
                            },
                        }}
                        leftSection={
                            <PageControlBar
                                noHorizontalPadding={true}
                                tabs={{
                                    selectedTab: activeTab,
                                    onChangeTab: (newTab: string) => {
                                        setActiveTab(newTab);
                                        history.replace({
                                            pathname: location.pathname,
                                            search: `?tab=${newTab.toLowerCase()}`,
                                        });
                                    },
                                    options: [
                                        {
                                            id: 'ACTIVE',
                                            label: 'Active',
                                            icon: LightbulbRegularIcon,
                                            count: String(activeCount),
                                        },
                                        {
                                            id: 'DRAFT',
                                            label: 'Draft',
                                            icon: PenRulerRegularIcon,
                                            count: String(draftCount),
                                        },
                                    ],
                                }}
                            />
                        }
                    />
                )
            }
        >
            <SalesAutomationPipelineList
                activeTab={activeTab}
                stagesets={stagesets}
                isLoading={isLoading}
                errorMessage={errorMessage}
            />
            <CreatePipelineModal
                isOpen={isCreatingPipeline}
                onClose={() => {
                    setIsCreatingPipeline(false);
                }}
            />
        </PageContainer>
    );
}

// @ts-expect-error
export const SalesAutomationSetupHome = withRouter<Record<any, any>>(
    SalesAutomationSetupHomeComponent
);
